<template>
  <div class="auth-shell" :class="{ 'has-panel': showPanel }">
    <div class="auth-form-col">
      <div class="auth-card" :class="{ 'auth-card-wide': wide }">
        <slot />
      </div>
    </div>
    <div v-if="showPanel" class="auth-panel-col d-none d-lg-flex">
      <slot name="panel"><auth-promo-panel /></slot>
    </div>
  </div>
</template>

<script>
import AuthPromoPanel from "./auth-promo-panel.vue";

export default {
  name: "AuthShell",
  components: {
    AuthPromoPanel,
  },
  props: {
    showPanel: {
      type: Boolean,
      default: false,
    },
    wide: {
      type: Boolean,
      default: false,
    },
  },
};
</script>

<style scoped>
.auth-shell {
  min-height: 100vh;
  display: flex;
  background: #fff;
}

.auth-form-col {
  flex: 1;
  display: flex;
  justify-content: center;
  padding: 32px 20px;
  overflow-y: auto;
}

.auth-shell.has-panel .auth-form-col {
  height: 100vh;
}

.auth-panel-col {
  width: 50%;
  flex-shrink: 0;
  height: 100vh;
  overflow: hidden;
}

.auth-card {
  width: 100%;
  max-width: 400px;
  background: #fff;
  padding: 32px 30px;
  /* auto margins center the card but, unlike align-items: center,
     keep the top reachable when the card is taller than the viewport */
  margin: auto 0;
  height: fit-content;
  animation: auth-rise 420ms cubic-bezier(0.22, 1, 0.36, 1) both;
}

.auth-card-wide {
  max-width: 620px;
}

@keyframes auth-rise {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 991.98px) {
  .auth-shell.has-panel .auth-form-col {
    height: auto;
    min-height: 100vh;
  }
}

@media (prefers-reduced-motion: reduce) {
  .auth-card {
    animation: none;
  }
}
</style>

<!-- Shared form classes for slotted auth-page content. Unscoped on purpose:
     Vue 2 scoped styles cannot reach slot content, so every rule is
     namespaced under .auth-shell instead. -->
<style>
.auth-shell .auth-logo {
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
}

.auth-shell .auth-logo img {
  max-height: 50px;
  max-width: 180px;
  object-fit: contain;
}

.auth-shell .auth-title {
  font-size: 24px;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: #111827;
  text-align: center;
  margin: 0 0 6px;
}

.auth-shell .auth-subtitle {
  font-size: 14px;
  line-height: 1.5;
  color: #6b7280;
  text-align: center;
  margin: 0 0 24px;
}

.auth-shell .auth-eyebrow {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #6b7280;
}

.auth-shell .auth-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 6px;
}

.auth-shell .auth-field {
  display: block;
  width: 100%;
  height: 38px;
  padding: 9px 13px;
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  box-shadow: 0 1px 2px rgba(8, 11, 13, 0.05);
  font-size: 14px;
  color: #1f2937;
  outline: none;
  transition: border-color 120ms ease, box-shadow 120ms ease;
}

.auth-shell .auth-field::placeholder {
  color: #9ca3af;
}

.auth-shell .auth-field:focus {
  border-color: #019256;
  box-shadow: 0 0 0 3px rgba(1, 146, 86, 0.12);
}

.auth-shell .auth-btn-primary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  background: #019256;
  color: #fff;
  border: 0;
  border-radius: 8px;
  padding: 10px 18px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(1, 146, 86, 0.2);
  transition: background-color 120ms ease, transform 80ms ease, box-shadow 120ms ease;
}

.auth-shell .auth-btn-primary:hover {
  background: #017a48;
  color: #fff;
  box-shadow: 0 2px 6px rgba(1, 146, 86, 0.25);
  text-decoration: none;
}

.auth-shell .auth-btn-primary:active {
  transform: translateY(1px);
}

.auth-shell .auth-btn-primary:focus-visible {
  outline: 2px solid #019256;
  outline-offset: 2px;
}

.auth-shell .auth-btn-primary:disabled,
.auth-shell .auth-btn-primary.is-disabled {
  background: #9ca3af;
  box-shadow: none;
  cursor: not-allowed;
}

.auth-shell .auth-btn-ghost {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  background: #fff;
  color: #374151;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 9px 17px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(8, 11, 13, 0.05);
  transition: background-color 120ms ease, border-color 120ms ease, transform 80ms ease;
}

.auth-shell .auth-btn-ghost:hover {
  background: #f3f4f6;
  color: #111827;
  text-decoration: none;
}

.auth-shell .auth-btn-ghost:active {
  transform: translateY(1px);
}

.auth-shell .auth-btn-ghost:focus-visible {
  outline: 2px solid #019256;
  outline-offset: 2px;
}

.auth-shell .auth-divider {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 24px 0;
  font-size: 13px;
  color: #6b7280;
}

.auth-shell .auth-divider::before,
.auth-shell .auth-divider::after {
  content: "";
  flex: 1;
  height: 1px;
  background: #e5e7eb;
}

.auth-shell .auth-link {
  font-size: 13px;
  font-weight: 600;
  color: #019256;
  text-decoration: none;
  transition: color 120ms ease;
}

.auth-shell .auth-link:hover {
  color: #017a48;
  text-decoration: underline;
}

.auth-shell .auth-success-note {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  background: rgba(1, 146, 86, 0.08);
  border: 1px solid rgba(1, 146, 86, 0.25);
  border-radius: 8px;
  padding: 12px 14px;
  font-size: 13px;
  line-height: 1.5;
  color: #017a48;
  margin-top: 16px;
}

.auth-shell .auth-body-text {
  font-size: 14px;
  line-height: 1.6;
  color: #4b5563;
}

.auth-shell .auth-body-text a {
  color: #019256;
  font-weight: 600;
}

.auth-shell .auth-icon-badge {
  width: 56px;
  height: 56px;
  border-radius: 999px;
  background: rgba(1, 146, 86, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 18px;
}

.auth-shell .alert {
  border-radius: 8px;
  font-size: 13px;
}

.auth-shell .form-group {
  margin-bottom: 16px;
}
</style>
